import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import qs from 'qs';
import { useSearchParams } from 'react-router-dom';
import {
  Card, Form, InputGroup, Pagination,
} from 'react-bootstrap';
import { Icon, LoadingAnimation } from './Utilities';

function DataTable({
  url,
  title = null,
  subtitle = null,
  filters = {},
  columns = [],
  renderRow,
  noResultsMessage = "Sorry, we couldn't find any results.",
  update = null,
  className,
  searchEnabled = false,
}) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [paging, setPaging] = useState({ total: 0, perPage: 15, current: 1 });
  const [sortable, setSortable] = useState([]);
  const [sort, setSort] = useState({ by: null, dir: null });
  const [searchValue, setSearchValue] = useState(filters.q || '');
  const [searchParams, setSearchParams] = useSearchParams();

  const loadData = async (page) => {
    setLoading(true);

    const params = { ...filters };

    // If search is enabled and there's a search value, add it to params
    if (searchEnabled && searchValue) {
      params.q = searchValue;
    }

    params.page = page;
    params.perPage = 15;
    params.sort = sort;

    try {
      const response = await axios.get(url, {
        params,
        paramsSerializer: (thisParams) => qs.stringify(thisParams),
      });

      if (response.data.data) {
        setItems(response.data.data);
        setPaging({
          total: response.data.total,
          perPage: response.data.per_page,
          current: response.data.current_page,
        });
        setSortable(Array.isArray(response.data.sortable) ? response.data.sortable : []);
      } else {
        setItems(response.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchValue(newValue);

    // Update URL parameter
    if (searchEnabled) {
      const newParams = new URLSearchParams(searchParams);
      if (newValue) {
        newParams.set('q', newValue);
      } else {
        newParams.delete('q');
      }
      setSearchParams(newParams);
    }
  };

  const clearSearch = () => {
    setSearchValue('');

    // Clear URL parameter
    if (searchEnabled) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('q');
      setSearchParams(newParams);
    }
  };

  const paginationClick = (event, page) => {
    event.stopPropagation();
    loadData(page);
  };

  const updateSort = (key) => {
    setSort((currentSort) => ({
      by: key,
      dir: currentSort.by === key && currentSort.dir === 'asc' ? 'desc' : 'asc',
    }));
    setPaging((currentPaging) => ({ ...currentPaging, current: 1 }));
  };

  useEffect(() => {
    loadData(paging.current);
  }, [url, JSON.stringify(filters), sort, update]);

  // Sync searchValue with filters.q when it changes externally
  useEffect(() => {
    if (filters.q !== searchValue) {
      setSearchValue(filters.q || '');
    }
  }, [filters.q]);

  // Debounce search - trigger loadData after user stops typing
  useEffect(() => {
    if (!searchEnabled) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      loadData(1); // Reset to page 1 when searching
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchValue]);

  let noResults; let tableHead; let
    header;

  if (title) {
    header = (
      <Card.Header>
        {title}
        {subtitle ? (
          <span style={{ float: 'right' }} className="small text-muted">{subtitle}</span>
        ) : ''}
      </Card.Header>
    );
  }

  if (items.length === 0 && !loading) {
    noResults = (
      <tr>
        <td colSpan={columns.length}>
          <div className="alert alert-warning m-4">
            {noResultsMessage}
          </div>
        </td>
      </tr>
    );
  }

  const cols = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const idx in columns) {
    if (typeof columns[idx] === 'string') {
      let icon;
      const columnKey = sortable.includes(idx) ? idx : columns[idx];
      const isSortable = sortable.includes(columnKey);

      if (isSortable) {
        icon = 'sort';

        if (sort.by === columnKey) {
          icon = sort.dir === 'asc' ? 'sort-up' : 'sort-down';
        }
      }

      cols.push((
        <th
          key={idx}
          onClick={isSortable ? () => updateSort(columnKey) : undefined}
          style={isSortable ? { cursor: 'pointer' } : undefined}
        >
          {columns[idx]}
          {icon && <Icon icon={icon} append="ml-2" />}
        </th>
      ));
    }
  }

  if (cols.length) {
    tableHead = (
      <thead>
        <tr>
          {cols}
        </tr>
      </thead>
    );
  }

  return (
    <>
      <Card className={className} style={{ minHeight: (loading ? '100px' : '') }}>
        {header}

        {searchEnabled && (
          <Card.Body className="pb-0">
            <InputGroup className="mb-3">
              <InputGroup.Text>
                <Icon icon="search" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchValue}
                onChange={handleSearchChange}
              />
              {searchValue && (
                <InputGroup.Text
                  style={{ cursor: 'pointer' }}
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <Icon icon="times" />
                </InputGroup.Text>
              )}
            </InputGroup>
          </Card.Body>
        )}

        <LoadingAnimation loading={loading} />

        <table className="table mb-0">
          {tableHead}
          <tbody>
            {noResults}
            {items.map(renderRow)}
          </tbody>
        </table>
      </Card>

      <DataTablePagination
        total={paging.total}
        perPage={paging.perPage}
        currentPage={paging.current}
        onClick={paginationClick}
      />
    </>
  );
}

DataTable.propTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.node,
  subtitle: PropTypes.node,
  filters: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  columns: PropTypes.arrayOf(PropTypes.string),
  renderRow: PropTypes.func.isRequired,
  noResultsMessage: PropTypes.string,
  update: PropTypes.any, // eslint-disable-line react/forbid-prop-types
  className: PropTypes.string,
  searchEnabled: PropTypes.bool,
};

DataTable.defaultProps = {
  title: null,
  subtitle: null,
  filters: {},
  columns: [],
  noResultsMessage: "Sorry, we couldn't find any results.",
  update: null,
  className: undefined,
  searchEnabled: false,
};

function DataTablePagination({
  total, perPage, currentPage, onClick,
}) {
  const max = Math.ceil(total / perPage);
  const pages = [];

  if (max === 1) {
    return null;
  }

  if (currentPage > 1) {
    pages.push(
      <Pagination.Prev key="prev" onClick={(e) => onClick(e, currentPage - 1)} style={{ whiteSpace: 'nowrap' }}>
        &laquo; Prev
      </Pagination.Prev>,
    );
  }

  for (let i = 1; i <= max; i += 1) {
    pages.push(
      <Pagination.Item key={i} active={i === currentPage} onClick={(e) => onClick(e, i)}>
        {i}
      </Pagination.Item>,
    );
  }

  if (currentPage < max) {
    pages.push(
      <Pagination.Next key="next" onClick={(e) => onClick(e, currentPage + 1)} style={{ whiteSpace: 'nowrap' }}>
        Next &raquo;
      </Pagination.Next>,
    );
  }

  if (pages.length === 0) {
    return '';
  }

  return (
    <Pagination className="mt-4" style={{ overflowX: 'scroll' }}>
      {pages}
    </Pagination>
  );
}

DataTablePagination.propTypes = {
  total: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
};

export default DataTable;
